(ns practitest.frontend.import-ticket-modal.main
  (:require
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [format-translation
                                             get-translation] :as i18n]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.re-frame :refer [partial-dispatch
                                                 partial-dispatch-sync
                                                 partial-subscribe]]
    [practitest.frontend.common.tooltip :as common-tooltip]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.import-ticket-modal.events :as import-ticket-events]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.import-ticket-modal.subs :as import-ticket-subs]
    [practitest.frontend.issue.bugtracker :as bugtracker]
    [practitest.frontend.common.bugtrackers :as bugtrackers]
    [practitest.frontend.common.service-now :as service-now]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.forms3.entity-form.fields :as fields]
    [practitest.frontend.forms3.entity-form.forms :as f3.forms]
    [practitest.frontend.components.selector :as selector2]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.routing.backend-routes :as broutes]
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(defn import-by-filter-tooltip
  [has-filters?]
    [common-tooltip/info-circle-tooltip
     (if has-filters?
       (get-translation "jira.import_modal.tooltip.has_filters" "Import tickets using Jira filters. All tickets matching the filter will be automatically updated in PractiTest.")
       (get-translation "jira.import_modal.tooltip.no_filters" "Your JIRA user account has no filters"))])

(defn has-filters? [pv-filters]
  (cond
    (map? pv-filters) (seq (:data pv-filters))
    (vector? pv-filters) (seq pv-filters)
    :else false))

(defn radio-button [{:keys [name label checked? disabled? value on-change]}]
  [:div.form-check.form-check-inline
   [:input.form-check-input
    {:type "radio"
     :name name
     :value value
     :checked checked?
     :disabled disabled?
     :on-change on-change}]
   [:label.form-check-label label]])

(defn import-by-radio-btns [{:keys [local-state entity-type]}]
  (let [pv-filters @(re-frame/subscribe [::import-ticket-subs/get-import-modal-jira-filters])
        has-filters? (has-filters? pv-filters)
        default-value (if has-filters? "by-filter" "by-key")]
    (swap! local-state #(if (nil? (get-in % [:options :import-by]))
                          (assoc-in % [:options :import-by] default-value)
                          %))
    (letfn [(mk-handler [path]
              (fn [e]
                (mixpanel/track "switched by key/filter - Import Ticket Modal" {"Entity" entity-type})
                (swap! local-state assoc-in path (-> e .-target .-value))
                (re-frame/dispatch [::import-ticket-events/clear-notifications])))
            (checked? [path value]
              (= value (get-in @local-state path)))
            (disabled? [value has-filters?]
              (and
                (= value "by-filter")
                (not has-filters?)))]
      [:div.col.mb-4.fs-14.d-flex.flex-column.align-items-center
       [:label.form-label.pe-3 ""]
       [:div.flex-row.align-items-center
        [:div.radio-container.pt-6
         (into [:<>]
               (for [[value label] [["by-key" (get-translation "jira.import_modal.import_by_key" "Import by Jira Key")]
                                    ["by-filter" (get-translation "jira.import_modal.import_by_filter" "Import by Jira Filter")]]]
                 ^{:key value}
                 [radio-button {:name "import-from-jira-modal-radio"
                                :label label
                                :value value
                                :checked? (checked? [:options :import-by] value)
                                :disabled? (disabled? value has-filters?)
                                :on-change (mk-handler [:options :import-by])}]))]
        [import-by-filter-tooltip has-filters?]]])))

(defn import-modal-dropdown [{:keys [possible-values selected-value placeholder label on-change on-search-change]}]
  [:div.g2-field-container
   {:class (dom/classes-pairs :fixed-width false :field-2 true)}
   [:span.fs-14 label]
   [selector2/new-select {:items       possible-values
                          :id          :select-project
                          :label-fn    :label
                          :key-fn      :id
                          :placeholder placeholder
                          :multi?      false
                          :tags?       false
                          :value       @selected-value
                          :on-change   on-change
                          :on-search-change on-search-change}]])

(defn import-modal-text-input [{:keys [bugtracker bugtracker-name] :as modal-props}]
  (let [field-id :import-by-key
        text-field-props {:type "text"
                          :placeholder (str (get-translation "bugtracker.import_modal.placholder.type" "Type ")
                                             bugtracker-name
                                             (get-translation "bugtracker.import_modal.placholder.text"
                                                              " key (For more than one key, use comma separations)"))
                          :field-id field-id
                          :autofocus? true
                          :get-field-state (partial-subscribe [::f3.forms/field-state field-id])
                          :set-field-state (partial-dispatch-sync [::import-ticket-events/set-field-state])}]
    (fn []
      (let [field-value @(re-frame/subscribe [::import-ticket-subs/get-import-modal-text-field-value field-id])
            text-field-props (assoc text-field-props :value field-value)]
        [:div.import-modal-text-input
          (when (bugtrackers/clickup? bugtracker)
            [:span.fs-14 (get-translation "clickup.import_modal.import_by_key" "Import by ClickUp Key")])
          (when (bugtrackers/azure? bugtracker)
            [:span.fs-14 (get-translation "azure.import_modal.import_by_key" "Import by Azure Key")])
         (when (bugtrackers/youtrack? bugtracker)
           [:span.fs-14 (get-translation "youtrack.import_modal.import_by_key" "Import by Youtrack Key")])
         (when (bugtrackers/service-now? bugtracker)
           [:span.fs-14 (get-translation "servicenow.import_modal.import_by_key" "Import by ServiceNow Ticket Number")])
          [fields/text-input text-field-props]]))))

(defn import-modal-checkbox-input []
  (let [field-id :filter-auto-import
        checkbox-field-props {:type "checkbox"
                              :field-id field-id}]
    (fn []
      (let [field-value @(re-frame/subscribe [::import-ticket-subs/get-import-modal-checkbox-field-value field-id])
            checkbox-field-props (assoc checkbox-field-props :value field-value)]
        [:div.import-modal-checkbox-input
         [fields/checkbox-control-editing (assoc checkbox-field-props
                                            :on-change #(re-frame/dispatch [::import-ticket-events/set-field-state
                                                                            field-id
                                                                            {:value (if (.-checked (.-target %))
                                                                                      "yes"
                                                                                      "no")}]))]]))))

(defn get-bugtracker-params [{:keys [local-state bugtracker entity-type] :as modal-props}]
  (let [import-by-filter?  (= "by-filter" (get-in @local-state [:options :import-by]))
        ids-to-import      @(re-frame/subscribe [::import-ticket-subs/get-import-modal-text-field-value :import-by-key])
        filter-to-import   @(re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :import-by-filter])
        filter-auto-import @(re-frame/subscribe [::import-ticket-subs/get-import-modal-checkbox-field-value :filter-auto-import])
        base-params        {:entity-type entity-type
                            :new_import_modal_ui true}]

    (merge base-params
           (cond
             (bugtrackers/jira? bugtracker)
             {:import_by_selection (when import-by-filter? "import_by_filterId")
              :jira_filter_id (:id filter-to-import)
              :jira_issue_id ids-to-import
              :filter-auto-import filter-auto-import}

             (bugtrackers/clickup? bugtracker)
             {:issue_id ids-to-import}

             (bugtrackers/azure? bugtracker)
             {:work_item_id ids-to-import}

             (bugtrackers/pivotal? bugtracker)
             {:story_id ids-to-import}

             (bugtrackers/youtrack? bugtracker)
             {:issue_id ids-to-import}

             (bugtrackers/service-now? bugtracker)
             {:issue_id ids-to-import
              :table    (:id @(re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :service-now-table]))}))))

(defn get-bugtracker-uri [{:keys [bugtracker entity-type] :as modal-props}]
  (cond
    (bugtrackers/jira? bugtracker)
    (broutes/get-url (if (= entity-type :requirement)
                       "requirement_integrations#import_from_jira"
                       "issues#import_from_jira")
                     {:p_id @(re-frame/subscribe [::subs/current-project-id])})

    (bugtrackers/clickup? bugtracker)
    (broutes/get-url (if (= entity-type :requirement)
                       "requirement_integrations#import_from_click_up"
                       "issues#import_from_click_up")
                     {:p_id @(re-frame/subscribe [::subs/current-project-id])})

    (bugtrackers/azure? bugtracker)
    (broutes/get-url (if (= entity-type :requirement)
                       "requirement_integrations#import_from_azure_devops"
                       "issues#import_from_azure_devops")
                     {:p_id @(re-frame/subscribe [::subs/current-project-id])})

    (bugtrackers/pivotal? bugtracker)
    (broutes/get-url (if (= entity-type :requirement)
                       "requirement_integrations#import_from_pivotal"
                       "issues#import_from_pivotal")
                     {:p_id @(re-frame/subscribe [::subs/current-project-id])})

    (bugtrackers/youtrack? bugtracker)
    (broutes/get-url (if (= entity-type :requirement)
                       "requirement_integrations#import_from_youtrack"
                       "issues#import_from_youtrack")
                     {:p_id  @(re-frame/subscribe [::subs/current-project-id])})

    (bugtrackers/service-now? bugtracker)
    (broutes/get-url (if (= entity-type :requirement)
                       "requirement_integrations#import_from_service_now"
                       "issues#import_from_service_now")
                     {:p_id @(re-frame/subscribe [::subs/current-project-id])})
    ))

(defn get-open-modal-query-event
  [bugtracker]
  (cond
    (bugtrackers/jira? bugtracker)
    ::import-ticket-events/fetch-import-modal-jira-filters

    (or (bugtrackers/clickup? bugtracker) (bugtrackers/youtrack? bugtracker) (bugtrackers/azure? bugtracker))
    ::import-ticket-events/check-user-linked

    (bugtrackers/service-now? bugtracker)
    ::import-ticket-events/fetch-service-now-types

    :else
    nil))

(defn import-tickets-button [{:keys [local-state bugtracker entity-type] :as modal-props}]
  (fn [{:keys [on-import]}]
    (let [import-by-filter (= "by-filter" (get-in @local-state [:options :import-by]))
          ids-to-import @(re-frame/subscribe [::import-ticket-subs/get-import-modal-text-field-value :import-by-key])
          filter-to-import @(re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :import-by-filter])
          service-now-table @(re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :service-now-table])

          params (get-bugtracker-params modal-props)
          uri (get-bugtracker-uri modal-props)
          payload [uri params]

          loading? (or @(re-frame/subscribe [::subs/progress? (get-open-modal-query-event bugtracker)])
                       @(re-frame/subscribe [::subs/progress? ::import-ticket-events/import-tickets]))
          disabled (or (and (bugtrackers/service-now? bugtracker) (nil? service-now-table))
                       (if import-by-filter
                          (nil? filter-to-import)
                          (str/blank? ids-to-import)))]
      [buttons/button
       {:disabled? disabled
        :primary? true
        :toolbar? true
        :on-click (fn [_e]
                    (when on-import
                      (mixpanel/track "Grid Actions" {"Action" "Import New Entity" "Entity" entity-type})
                      (on-import payload)))
        :loading? loading?
        :text (get-translation "generic.button.import" "Import")
        :size "large"}])))

(defn cancel-button
  [{:keys [close-dialog entity-type] :as modal-props}]
  [buttons/button
    {:on-click (fn []
                (mixpanel/track "close - Import Ticket Modal" {"Entity" entity-type})
                (re-frame/dispatch [::import-ticket-events/clear-fields])
                (re-frame/dispatch [::import-ticket-events/clear-notifications])
                (close-dialog))
    :toolbar? true
    :text (get-translation "generic.button.cancel" "Cancel")
    :size "large"}])

(defn import-modal-action-buttons
  [modal-props]
  [:div.right-selection.nowrap.add-cancel-component.import-tickets-modal
    [cancel-button modal-props]
    [import-tickets-button modal-props]])

(defn jira-section
  [{:keys [local-state] :as modal-props}]
  (fn []
    (let [import-by-filter (= "by-filter" (get-in @local-state [:options :import-by]))]
         [:<>
          [import-by-radio-btns modal-props]
          (if import-by-filter
            [:<>
             [import-modal-dropdown {:possible-values @(re-frame/subscribe [::import-ticket-subs/get-import-modal-jira-filters])
                                     :selected-value (re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :import-by-filter])
                                     :label ""
                                     :placeholder (get-translation "jira.import_modal.placholder.select" "Select a Filter")
                                     :on-change #(re-frame/dispatch [::import-ticket-events/set-field-state :import-by-filter %])}]
             ;[:div.flex-row.pt-3.pb-4.gap-12
             ;  [import-modal-checkbox-input]
             ;  [:div.fs-10
             ;    (get-translation "jira.import_modal.auto_sync_filter.checkbox_text" "Automatically import future tickets located in Jira under this filter. (This setting can be changed in Project settings > Integration > Auto Sync Filters)")]]
             ]
            [import-modal-text-input modal-props])])))

(defn clickup-section
  [modal-props]
  [import-modal-text-input modal-props])

(defn azure-section
  [modal-props]
    (fn []
      [:<>
        [import-modal-text-input modal-props]]))

(defn pivotal-section
  [modal-props]
    (fn []
      [:<>
        [import-modal-text-input modal-props]]))

(defn youtrack-section
  [modal-props]
    (fn []
      [:<>
        [import-modal-text-input modal-props]]))

(defn service-now-section
  [{:keys [entity-type] :as modal-props}]
  (fn []
    (let [types @(re-frame/subscribe [::import-ticket-subs/get-import-modal-service-now-types])
          items (map (fn [t] {:id (:id t) :label (:name t)}) types)
          selected-table (re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :service-now-table])
          searching? @(re-frame/subscribe [::subs/progress? ::import-ticket-events/search-service-now-types])]
      [:<>
       [import-modal-dropdown {:possible-values items
                               :selected-value  selected-table
                               :label           [:<>
                                                  (get-translation "servicenow.import_modal.ticket_type" "Ticket Type")
                                                  (when searching? [spinners/mux-spinner {:size :text}])]
                               :placeholder     (get-translation "servicenow.import_modal.placeholder.select_type" "Select a type")
                               :on-change       #(re-frame/dispatch [::import-ticket-events/set-field-state :service-now-table %])
                               :on-search-change (fn [term]
                                                    (when (service-now/search-term-long-enough? term)
                                                      (import-ticket-events/dispatch-service-now-search-debounced
                                                        entity-type term)))}]
       [import-modal-text-input modal-props]])))

(defn import-modal-body
  [{:keys [bugtracker] :as modal-props}]
  (fn []
    [:div.import-modal-body.mt-3
     (cond
       (bugtrackers/jira? bugtracker)
       [jira-section modal-props]

       (bugtrackers/clickup? bugtracker)
       [clickup-section modal-props]

       (bugtrackers/azure? bugtracker)
       [azure-section modal-props]

       (bugtrackers/pivotal? bugtracker)
       [pivotal-section modal-props]

       (bugtrackers/youtrack? bugtracker)
       [youtrack-section modal-props]

       (bugtrackers/service-now? bugtracker)
       [service-now-section modal-props]
       :else
       (js/console.warn "Invalid bugtracker type provided" bugtracker))

      [import-modal-action-buttons modal-props]]))

(defn get-refresh-span []
  [:span {:class "m-pseudolink"
          :on-click #(re-frame/dispatch [::import-ticket-events/refresh-modal])}
   (get-translation "bugtracker.import_modal.msgs.refresh" "Refresh")])

(def get-requirements-list-link
  [:a {:href (str (broutes/get-url-view "requirements#index"))
       :on-click (fn [_]
                   (re-frame/dispatch [::common-events/fetch-entity-list :requirement :requirements-grid])
                   (re-frame/dispatch [::refresh-modal])
                   (re-frame/dispatch [::import-ticket-events/toggle-import-from-bugtracker-modal]))}
   "Requirement List"])

(def get-issues-list-link
  [:a {:href (str (broutes/get-url-view "issues#index"))
       :on-click (fn [_]
                   (re-frame/dispatch [::common-events/fetch-entity-list :issue :issues-grid])
                   (re-frame/dispatch [::refresh-modal])
                   (re-frame/dispatch [::import-ticket-events/toggle-import-from-bugtracker-modal]))}
   "Issue List"])

(defn get-support-link []
  [:a {:href "mailto:support@practitest.com"} "Support"])

(defn notify-ticket-with-link [tickets message type toast-id-suffix class]
  (map (fn [ticket]
         (let [{:keys [requirement issue]} ticket
               entity (cond
                        requirement :requirement
                        issue :issue)
               edit-url (cond
                          requirement "requirements#edit"
                          issue "issues#edit")
               entity-data (get ticket entity)
               ticket-id (:display_id entity-data)
               entity-url (broutes/get-url-view edit-url {:id (get-in ticket [entity :id])})
               entity-link (str "<a href="entity-url">"ticket-id"</a>")
               body [:div
                     [:span.bold {:dangerouslySetInnerHTML
                             {:__html (format-translation
                                        message
                                        {:ticket_id ticket-id,
                                         :entity_link entity-link})}}]]
               id (str toast-id-suffix "-" ticket-id)]
           (notifications/toast-element body {:type type :id id :class class})))
       tickets))

(defn notify-errors-with-link [errors type toast-id-suffix class]
  (let [import-failed (get-translation "bugtracker.import_modal.msgs.key.error.import_failed" "Import failed.")
        you-can-click (get-translation "jira.import_modal.msgs.filter.error.you_can_click" "You can click ")
        and-try-again (get-translation "bugtracker.import_modal.msgs.key.error.and_try_again" " and try again or contact ")]
    (map (fn [error-key]
           (let [id (str toast-id-suffix)
                 body [:div
                       [:span.bold error-key " " import-failed]
                       (when (some? (error-key errors))
                         [:<>
                          [:br]
                          [:span {:dangerouslySetInnerHTML {:__html (error-key errors)} }]])
                       [:span you-can-click (get-refresh-span) and-try-again (get-support-link)]]]
             (notifications/toast-element body {:type type :id id :class class})))
         (keys errors))))

(defn notify-filter [message type toast-id-suffix class entity-type count]
  (let [id (str toast-id-suffix)
        issues-list-link get-issues-list-link
        requirements-list-link get-requirements-list-link

        entity-list (if (= :issue entity-type) issues-list-link requirements-list-link)

        selected-value @(re-frame/subscribe [::import-ticket-subs/get-import-modal-select-field-value :import-by-filter])
        filter-name (:label selected-value)
        body [:div
              [:span.bold
                (format-translation
                  message
                  {:filter_name filter-name,
                   :saved_count (:saved count),
                   :exist_count (:exist count)})]
              [:br]
              [:span
               "Go to " entity-list " to preview them or click "
               (get-refresh-span) " to import additional tickets."]]]
    [(notifications/toast-element body {:type type :id id :class class})]))

(defn show-import-from-bugtracker-modal [bugtracker entity-type]
  (mixpanel/track "Import from bugtracker modal opened")
  (cond
    (bugtrackers/jira? bugtracker)
    (re-frame/dispatch [::import-ticket-events/fetch-import-modal-jira-filters entity-type])

    (or (bugtrackers/clickup? bugtracker) (bugtrackers/youtrack? bugtracker) (bugtrackers/azure? bugtracker))
    (re-frame/dispatch [::import-ticket-events/check-user-linked nil])

    (bugtrackers/service-now? bugtracker)
    (re-frame/dispatch [::import-ticket-events/check-user-linked nil [::import-ticket-events/fetch-service-now-types entity-type]])

    :else
    (re-frame/dispatch [::import-ticket-events/toggle-import-from-bugtracker-modal])))

(defn notify-warnings [warnings type toast-id-suffix class]
  (let [grouped (group-by val warnings)]
    (map-indexed (fn [idx [msg entries]]
                   (let [keys-str (str/join ", " (map (comp name key) entries))
                         body [:div
                               [:span.bold keys-str " - " msg]]
                         id (str toast-id-suffix "-" idx)]
                     (notifications/toast-element body {:type type :id id :class class})))
                 grouped)))

(defn display-modal-messages [{:keys [local-state entity-type]} import-results toast-id]
  (let [import-by-filter (= "by-filter" (get-in @local-state [:options :import-by]))
        import-results-map (:import_results import-results)
        {:keys [exist saved not_found not_found_filter linked error warning]} import-results-map
        display-messages? (some not-empty [exist saved not_found not_found_filter linked error warning])

        key-suc-msg (get-translation "bugtracker.import_modal.msgs.key.success" "%{ticket_id} was successfully imported. Go to %{entity_link}")
        key-info-msg (get-translation "bugtracker.import_modal.msgs.key.info" "%{ticket_id} already exists in PractiTest. Go to %{entity_link}")

        filter-all-saved (and (seq saved) (not (seq exist)))
        filter-all-exist (and (seq exist) (not (seq saved)))
        filter-partial-import (and (seq exist) (seq saved))
        filter-import-error (or (seq not_found_filter) (seq error))

        count {:exist (count exist)
               :saved (count saved)}
        filter-suc-msg (get-translation "jira.import_modal.msgs.filter.success" "All tickets (%{suc_count}) in Filter %{filter_name} were successfully imported. ")
        filter-info-msg (get-translation "jira.import_modal.msgs.filter.info" "All tickets (%{ext_count}) in Filter %{filter_name} already exists in PractiTest. ")
        filter-partial-msg (get-translation "jira.import_modal.msgs.filter.partial" "%{suc_count} tickets in Filter %{filter_name} were successfully imported; %{ext_count} tickets were already in PractiTest. ")
        filter-all-imported (get-translation "jira.import_modal.msgs.key.info.all_imported"
                                             "All entities in the filter already exists in PractiTest.")]

    (when display-messages?
      (concat
      (if import-by-filter
        (concat
          (cond
            filter-all-exist
            (notify-filter filter-info-msg "info" (str toast-id "-exist") "in-modal-msg-info" entity-type count)
            filter-all-saved
            (notify-filter filter-suc-msg "success" (str toast-id "-saved") "in-modal-msg-success" entity-type count)
            filter-partial-import
            (notify-filter filter-partial-msg "success" (str toast-id "-partial") "in-modal-msg-success" entity-type count)
            filter-import-error
            (notify-filter filter-all-imported "info" (str toast-id "-exist") "in-modal-msg-info" entity-type count))
          (notify-warnings warning "warning" (str toast-id "-warning") "in-modal-msg-info"))
        (concat
          (notify-ticket-with-link saved key-suc-msg "success" (str toast-id "-saved") "in-modal-msg-success")
          (notify-ticket-with-link exist key-info-msg "info" (str toast-id "-exist") "in-modal-msg-info")
          (notify-errors-with-link error "error" (str toast-id "-error") "in-modal-msg-error")
          (notify-warnings warning "warning" (str toast-id "-warning") "in-modal-msg-info")))))))

(defn import-bugtracker-tickets-modal [container-ref {:keys [title logo show? hide!] :as modal-props}]
  (let [modal-state (reagent/atom {:expanded #{}})]
    (fn [_ modal-props]
      (let [modal-props (merge modal-props
                               {:close-dialog hide!
                                :local-state modal-state
                                :container-ref container-ref})
            import-results @(re-frame/subscribe [::import-ticket-subs/get-import-modal-notifications])
            notifications (display-modal-messages modal-props import-results :toast-id)]
        [modal/modal-with-notifications
         {:is-open? @show?
          :on-request-close hide!
          :container-ref container-ref
          :class "import-bugtracker-tickets-modal"
          :close-button? true}

         ;; header
         [:span.import-bugtracker-tickets-modal-header
          [:div.d-flex.gap-12 logo title]]

         ;; body
         [import-modal-body modal-props]

         ;; notifications
         notifications]))))

(defn import-bugtracker-tickets-modal-options [{:keys [current-entity grid-state]}]
  (let [current-project @(re-frame/subscribe [::subs/current-project])
        bugtracker (:integrated_bugtracker current-project)
        bugtracker-name (bugtrackers/get-label bugtracker)
        title-prefix (get-translation "bugtracker.import_modal.title" " Import Ticket from ")]
    {:entity-type current-entity
     :grid-state grid-state
     :show? (re-frame/subscribe [::import-ticket-subs/show-import-from-bugtracker-modal?])
     :hide! #(re-frame/dispatch [::import-ticket-events/toggle-import-from-bugtracker-modal])
     :bugtracker bugtracker
     :bugtracker-name bugtracker-name
     :logo [bugtracker/bugtracker-logo]
     :title  (str title-prefix bugtracker-name)
     :on-import (partial-dispatch [::import-ticket-events/import-tickets grid-state])}))
